/**
 * Privacy Policy Content
 * Edit this file to update the privacy policy displayed on the /privacy page.
 * Each section has a title and content (supports multiple paragraphs).
 */

interface Subsection {
  title: string
  content: string[]
}

interface Section {
  title: string
  content: string[]
  subsections?: Subsection[]
}

interface PrivacyContent {
  lastUpdated: string
  sections: Section[]
}

export const privacyContent: PrivacyContent = {
  lastUpdated: 'January 2025',

  sections: [
    {
      title: 'When You Visit Our Website',
      content: [
        'When you browse the Bandos marketing website without logging in, we collect minimal information.',
      ],
      subsections: [
        {
          title: 'Analytics',
          content: [
            'We use PostHog, an EU-based analytics service, to understand how visitors use our website. This is only activated if you click "Accept" on our cookie banner.',
            'If you accept cookies, we collect: pages you visit and how long you spend on them, how far you scroll on each page, which buttons and links you click, which blog posts you read and for how long, and basic technical information like browser type and screen size.',
            'We do not collect your name, email address, or any other personal details from browsing. We do not use advertising cookies or share browsing data with third parties.',
            'If you reject cookies, no analytics data is collected. You can still use the website normally.',
          ],
        },
        {
          title: 'What We Store in Your Browser',
          content: [
            "We store your cookie preference in your browser's local storage so we remember your choice. This is the only information stored locally.",
          ],
        },
        {
          title: 'No Account Required',
          content: [
            'You do not need to create an account to browse our website or read our blog. Account creation and login happen in our web application at www.bandos.ai/app, which has its own authentication and data handling described below.',
          ],
        },
      ],
    },
    {
      title: 'Information We Collect',
      content: [
        'When you register for Bandos, we collect your email address, password (securely hashed), and optionally your full name. We also record your acceptance of our terms and newsletter preferences.',
        'During signup, we ask optional survey questions about your role, use cases, and current tools to help us understand our users and improve the product.',
        'When you use our service, we collect technical information such as your IP address, browser type, and device information for error tracking and security purposes.',
      ],
    },
    {
      title: 'How We Use Your Information',
      content: [
        'We use your information to provide and maintain our services, authenticate your account, and personalize your experience.',
        'Survey data helps us understand how people use Bandos and guides product development decisions.',
        'We may use your email to send important service updates. If you opt in, we may also send newsletters and product announcements.',
      ],
    },
    {
      title: 'Your Content & Data',
      content: [
        'The ideas, mind maps, and projects you create remain yours. We do not claim ownership of your content and will not share it with third parties without your consent.',
        'Your project data is stored securely in our database. We implement row-level security to ensure you can only access your own data.',
      ],
    },
    {
      title: 'AI Processing',
      content: [
        'When you use AI features, we send minimal context to our AI providers (Anthropic Claude and OpenAI) to generate suggestions. This includes the current node content, relevant stakeholder or venture context, and your project title.',
        'We do not send your email, user ID, or full project data to AI services. Only the information needed to generate relevant suggestions is transmitted.',
        "Your projects are not used to train AI models. AI-generated content is created based on your inputs and the model's general knowledge.",
      ],
    },
    {
      title: 'Cookies & Local Storage',
      content: [
        'We use essential cookies managed by Supabase for authentication to keep you logged in securely.',
        "We store your theme preference (light/dark mode) in your browser's local storage.",
        'We do not use third-party analytics or advertising cookies.',
      ],
    },
    {
      title: 'Third-Party Integrations',
      content: [
        'If you connect Bandos to Trello or Jira, we securely store your OAuth tokens (encrypted) to sync your data with those platforms.',
        'When you sync content to these platforms, the relevant node information (labels, priorities, descriptions) is sent to them according to your configuration.',
        'You can disconnect integrations at any time, which removes the stored tokens.',
      ],
    },
    {
      title: 'Error Logging & Feedback',
      content: [
        'We collect error logs to diagnose and fix issues. These logs may include error messages, stack traces, the URL where the error occurred, and your browser information.',
        'When you submit feedback, we collect your message along with contextual information like page URL, browser type, and screen size to help us understand and address your feedback.',
        'Sensitive information like passwords and API tokens is automatically redacted from logs.',
      ],
    },
    {
      title: 'Data Sharing',
      content: [
        'We do not sell your personal information.',
        'Our service providers include Supabase (database and authentication), Anthropic and OpenAI (AI features), and your connected integrations (Trello, Jira).',
        'We may disclose information if required by law or to protect the rights, safety, and property of Bandos, our users, or others.',
      ],
    },
    {
      title: 'Data Retention',
      content: [
        'We retain your data for as long as your account is active. Token usage records are kept for billing and quota tracking purposes.',
        'You can request deletion of your account and associated data at any time by contacting us.',
        'Some information (error logs, usage records) may be retained for legal, security, or operational purposes even after account deletion.',
      ],
    },
    {
      title: 'Security',
      content: [
        'Passwords are securely hashed using industry-standard algorithms (managed by Supabase Auth).',
        'Integration OAuth tokens are encrypted before storage.',
        'We use row-level security policies to ensure users can only access their own data.',
      ],
    },
    {
      title: 'Your Rights',
      content: [
        'You can view and edit your profile information, theme preferences, and AI generation settings from your account settings.',
        'You can view your token usage history and manage your connected integrations.',
        'To request a full data export or account deletion, contact us at privacy@bandos.ai.',
      ],
    },
    {
      title: 'Changes to This Policy',
      content: [
        'We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our platform or by email.',
      ],
    },
    {
      title: 'Contact Us',
      content: [
        'If you have questions about this Privacy Policy or our data practices, please contact us at privacy@bandos.ai.',
      ],
    },
  ],
}
